import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Pen, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber, getUserName } from 'helpers';
import { useAPI, useDebounce, useGetPilgrim, useGetUser } from 'hooks';
import { Badge } from 'components/ui/badge';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Spinner } from 'components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { formatPilgrimDate } from './PilgrimDepositItem';

const formatAmount = (amount) => `${formatNumber(amount)} F CFA`;

const hasExistingDeposits = (trip) => Array.isArray(trip.deposits) && trip.deposits.length > 0;

const getPendingDeposits = (trip) =>
  Array.isArray(trip.deposits) ? trip.deposits.filter((deposit) => deposit.is_invoice_paid === false) : [];

const isTripFullyPaid = (trip) => trip.deposit_transferred >= trip.cart_total;

const getAutomaticBalance = (trip) => {
  if (!hasExistingDeposits(trip) || isTripFullyPaid(trip)) return null;

  const total = Number(trip.cart_total);
  const deposited = Number(trip.deposit_total);
  if (!Number.isFinite(total) || !Number.isFinite(deposited)) return 0;
  return Math.max(0, total - deposited);
};

const getSelectedAmount = (trip, amount, registrationDeposit) => {
  if (isTripFullyPaid(trip)) return 0;

  const automaticBalance = getAutomaticBalance(trip);
  if (automaticBalance !== null) return automaticBalance;
  if (amount === null || amount === undefined || String(amount).trim() === '') return null;

  const numericAmount = Number(amount);
  const total = Number(trip.cart_total);
  const minimum = Number(registrationDeposit);
  const isStandardChoice = numericAmount === minimum || numericAmount === total;
  const isValidCustomAmount =
    trip.self_registered && Number.isInteger(numericAmount) && numericAmount >= minimum && numericAmount <= total;

  return isStandardChoice || isValidCustomAmount ? numericAmount : null;
};

const getCustomAmountError = (value, minimum, maximum) => {
  if (String(value).trim() === '') return 'Champ non valide';
  if (!/^\d+$/.test(String(value))) return 'Le montant doit être un nombre entier';

  const numericValue = Number(value);
  if (numericValue < Number(minimum)) return `Le montant minimum est de ${formatAmount(minimum)}`;
  if (numericValue > Number(maximum)) return `Le montant maximum est de ${formatAmount(maximum)}`;
  return null;
};

const getInitialAmount = (trip) =>
  !isTripFullyPaid(trip) && !hasExistingDeposits(trip) ? Number(trip.cart_total) : '';

const getAmountMode = (amount, advanceDeposit, cartTotal) => {
  if (amount === null || amount === undefined || String(amount).trim() === '') return null;

  const numericAmount = Number(amount);
  if (numericAmount === Number(cartTotal)) return 'total';
  if (numericAmount === Number(advanceDeposit)) return 'deposit';
  return 'custom';
};

const getInitialPaymentState = (pilgrims) =>
  Object.fromEntries(pilgrims.map((trip) => [trip.id, { amount: getInitialAmount(trip), customOpen: false }]));

const depositPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  created: PropTypes.string,
  payment_date: PropTypes.string,
  invoice_code: PropTypes.string,
  is_invoice_paid: PropTypes.bool,
});
const depositsPropType = PropTypes.arrayOf(depositPropType).isRequired;

const DepositMetadata = ({ deposit, showAmount = true }) => {
  const amount = Number(deposit.amount);
  const paidDate = formatPilgrimDate(deposit.payment_date);
  const initiatedDate = formatPilgrimDate(deposit.created);
  const date = deposit.is_invoice_paid && paidDate ? paidDate : initiatedDate;
  const dateLabel =
    deposit.is_invoice_paid && paidDate ? 'Payé le' : deposit.is_invoice_paid ? 'Facturé le' : 'Date d’initiation';

  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-xs sm:grid-cols-3">
      {showAmount && (
        <div>
          <dt className="text-gray-500">Montant</dt>
          <dd className="mt-0.5 font-semibold tabular-nums text-gray-900">
            {Number.isFinite(amount) ? formatAmount(amount) : '-'}
          </dd>
        </div>
      )}
      {date && (
        <div>
          <dt className="text-gray-500">{dateLabel}</dt>
          <dd className="mt-0.5 font-medium text-gray-900">{date}</dd>
        </div>
      )}
      {deposit.invoice_code && (
        <div>
          <dt className="text-gray-500">Facture</dt>
          <dd className="mt-0.5 break-all font-mono font-medium text-gray-900">{deposit.invoice_code}</dd>
        </div>
      )}
    </dl>
  );
};

DepositMetadata.propTypes = {
  deposit: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created: PropTypes.string,
    payment_date: PropTypes.string,
    invoice_code: PropTypes.string,
    is_invoice_paid: PropTypes.bool,
  }).isRequired,
  showAmount: PropTypes.bool,
};

const PreviousPaymentChoice = ({ id, deposits, advanceDeposit, cartTotal }) => {
  const total = deposits.reduce((sum, deposit) => {
    const amount = Number(deposit.amount);
    return Number.isFinite(amount) ? sum + amount : sum;
  }, 0);
  const isFullPayment = deposits.length === 1 && total === Number(cartTotal);
  const isAcompte = !isFullPayment && deposits.length === 1 && total === Number(advanceDeposit);
  const allPaid = deposits.every((deposit) => deposit.is_invoice_paid === true);
  const title = isFullPayment
    ? 'Paiement intégral'
    : isAcompte
    ? 'Acompte'
    : deposits.length === 1
    ? 'Montant personnalisé'
    : 'Paiements précédents';

  return (
    <div
      data-option={`previous-${id}`}
      className="h-full min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50/80 text-gray-600"
    >
      <div className="flex min-h-[76px] items-start gap-3 px-4 py-3">
        <RadioGroupItem value="previous" disabled aria-label={`${title} déjà facturé`} className="mt-1 shrink-0" />
        <div className="min-w-0 flex-1">
          <span className="block text-xs font-medium text-gray-500">{title}</span>
          <strong className="mt-0.5 block whitespace-nowrap text-base font-semibold tabular-nums text-gray-900">
            {formatAmount(total)}
          </strong>
          <Badge
            variant={allPaid ? 'success' : 'pending'}
            className="mt-2 w-fit max-w-full whitespace-normal text-left leading-4"
          >
            {allPaid ? 'Payé' : 'En attente de confirmation'}
          </Badge>
        </div>
      </div>
      <ul className="space-y-2 border-t border-gray-200 bg-white/70 px-4 py-3">
        {deposits.map((deposit, index) => (
          <li key={deposit.id || deposit.invoice_code || index}>
            <DepositMetadata deposit={deposit} showAmount={deposits.length > 1} />
          </li>
        ))}
      </ul>
    </div>
  );
};

PreviousPaymentChoice.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  deposits: depositsPropType,
  advanceDeposit: PropTypes.number,
  cartTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const Initials = ({ firstname, lastname }) => (
  <Avatar className="size-8 shrink-0">
    <AvatarFallback className="bg-hajj-primary/10 text-xs font-semibold text-hajj-primary">
      {`${firstname?.[0] || ''}${lastname?.[0] || ''}` || '?'}
    </AvatarFallback>
  </Avatar>
);

Initials.propTypes = { firstname: PropTypes.string, lastname: PropTypes.string };

const AmountChoice = ({ id, value, title, amount, disabled, selected = false, description, onEdit }) => (
  <div
    data-option={`${value}-${id}`}
    className={`flex h-full min-h-16 min-w-0 items-center gap-2 rounded-lg border px-4 py-3 transition-colors ${
      disabled
        ? 'border-gray-200 bg-gray-50 text-gray-500'
        : selected
        ? 'border-hajj-primary bg-[#f4faf6] shadow-[0_0_0_1px_rgba(49,129,83,0.12)]'
        : 'border-gray-200 bg-white hover:border-hajj-primary hover:bg-[#f7fbf8]'
    }`}
  >
    <label
      htmlFor={`invoice-amount-${value}-${id}`}
      className={`flex min-w-0 flex-1 items-center gap-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <RadioGroupItem
        id={`invoice-amount-${value}-${id}`}
        value={value}
        disabled={disabled}
        className={`shrink-0 ${
          selected ? 'border-hajj-primary text-hajj-primary focus-visible:ring-hajj-primary' : ''
        }`}
      />
      <span className="min-w-0 flex-1">
        <span className={`block text-xs font-medium ${selected ? 'text-hajj-primary' : 'text-gray-600'}`}>{title}</span>
        <strong className="mt-0.5 block whitespace-nowrap text-base font-semibold tabular-nums text-gray-900">
          {formatAmount(amount)}
        </strong>
        {description && (
          <span
            className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${
              selected ? 'text-hajj-primary' : 'text-gray-500'
            }`}
          >
            {selected && <CheckCircle2 className="size-3.5 shrink-0" aria-hidden="true" />}
            {description}
          </span>
        )}
      </span>
    </label>
    {onEdit && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0 text-gray-500"
            onClick={onEdit}
            disabled={disabled}
            aria-label={`Saisir un montant personnalisé pour ${title.toLowerCase()}`}
          >
            <Pen className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent portalled className="font-[Inter,sans-serif]">
          Saisir un montant personnalisé
        </TooltipContent>
      </Tooltip>
    )}
  </div>
);

AmountChoice.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.oneOf(['deposit', 'total', 'remaining']).isRequired,
  title: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  selected: PropTypes.bool,
  description: PropTypes.string,
  onEdit: PropTypes.func,
};

const PreviousAndRemainingChoices = ({ id, name, deposits, advanceDeposit, cartTotal, remaining, isMutating }) => {
  const hasRemaining = remaining > 0;
  return (
    <RadioGroup
      className="mt-3 grid items-stretch gap-2 sm:grid-cols-2"
      value={hasRemaining ? 'remaining' : ''}
      aria-label={`Montant restant à facturer pour ${name}`}
    >
      <PreviousPaymentChoice id={id} deposits={deposits} advanceDeposit={advanceDeposit} cartTotal={cartTotal} />
      <AmountChoice
        id={id}
        value="remaining"
        title="Restant"
        amount={remaining}
        disabled={isMutating || !hasRemaining}
        selected={hasRemaining}
        description={hasRemaining ? 'Seule option disponible' : 'Aucun montant à facturer'}
      />
    </RadioGroup>
  );
};

PreviousAndRemainingChoices.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  deposits: depositsPropType,
  advanceDeposit: PropTypes.number,
  cartTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  remaining: PropTypes.number.isRequired,
  isMutating: PropTypes.bool,
};

const InvoicePilgrimRow = ({
  trip,
  advanceDeposit,
  amount,
  showCustom,
  isPilgrim,
  isMutating,
  onSelectAmount,
  onStartCustom,
  onCommitCustom,
  onCancelCustom,
  onRemove,
}) => {
  const [customValue, setCustomValue] = useState('');
  const [customError, setCustomError] = useState(null);
  const customInputRef = useRef(null);
  const {
    id,
    firstname,
    lastname,
    npi,
    categoryName,
    cartTotal,
    selfRegistered,
    deposits,
    depositTransferred,
    isInvoiceFullyPaid,
  } = useGetPilgrim(trip);
  const name = getUserName(firstname, lastname);
  const hasDeposits = Array.isArray(deposits) && deposits.length > 0;
  const pendingDeposits = getPendingDeposits(trip);
  const hasPendingPayment = pendingDeposits.length > 0;
  const automaticBalance = getAutomaticBalance(trip);
  const requiresChoice = !isInvoiceFullyPaid && !hasDeposits;
  const amountMode = getAmountMode(amount, advanceDeposit, cartTotal);
  const selectedOption = !showCustom && (amountMode === 'deposit' || amountMode === 'total') ? amountMode : undefined;

  const openCustomInput = () => {
    setCustomValue(amountMode === 'custom' ? String(amount) : isPilgrim ? '' : String(advanceDeposit));
    setCustomError(null);
    onStartCustom(id);
  };

  useEffect(() => {
    if (showCustom) customInputRef.current?.focus();
  }, [showCustom]);

  const validateCustomAmount = () => {
    const error = getCustomAmountError(customValue, advanceDeposit, cartTotal);
    setCustomError(error);
    if (error) return;

    onCommitCustom(id, Number(customValue));
  };

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-3 sm:p-4">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_2.25rem] gap-x-2 gap-y-2 sm:flex sm:items-start sm:gap-3">
        <div className="flex min-w-0 items-start gap-2.5 sm:flex-1">
          <Initials firstname={firstname} lastname={lastname} />
          <div className="min-w-0">
            <h3 className="break-words text-sm font-semibold text-gray-900">{name}</h3>
            <p className="mt-1 flex flex-wrap gap-x-2 text-xs text-gray-500">
              {npi && <span>NPI {npi}</span>}
              {categoryName && <span>· {categoryName}</span>}
            </p>
          </div>
        </div>
        <div className="col-span-2 row-start-2 text-left sm:ml-auto sm:text-right">
          <span className="block text-xs text-gray-500">Payé</span>
          <strong className={`text-sm tabular-nums ${depositTransferred > 0 ? 'text-emerald-700' : 'text-gray-900'}`}>
            {depositTransferred === null ||
            depositTransferred === undefined ||
            (typeof depositTransferred === 'string' && !depositTransferred.trim()) ||
            !Number.isFinite(Number(depositTransferred))
              ? '-'
              : formatAmount(depositTransferred)}
          </strong>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="col-start-2 row-start-1 size-9 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          onClick={() => onRemove(id)}
          disabled={isMutating}
          aria-label={`Retirer ${name} de la facture`}
          title="Retirer de la facture"
        >
          <X className="size-4" />
        </Button>
      </div>

      {isInvoiceFullyPaid && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3">
          <div>
            <p className="text-sm font-semibold text-emerald-800">Déjà soldé</p>
            <p className="mt-0.5 text-xs text-emerald-700">Aucun nouveau montant à facturer.</p>
          </div>
          <Badge variant="success">Payé</Badge>
        </div>
      )}

      {!isInvoiceFullyPaid && hasDeposits && (automaticBalance > 0 || hasPendingPayment) && (
        <PreviousAndRemainingChoices
          id={id}
          name={name}
          deposits={deposits}
          advanceDeposit={advanceDeposit}
          cartTotal={cartTotal}
          remaining={automaticBalance}
          isMutating={isMutating}
        />
      )}

      {!isInvoiceFullyPaid && hasDeposits && automaticBalance === 0 && !hasPendingPayment && (
        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
          <p className="text-sm font-semibold text-gray-700">Aucun nouveau montant à facturer.</p>
        </div>
      )}

      {requiresChoice && (
        <>
          <RadioGroup
            className="mt-3 grid gap-2 sm:grid-cols-2"
            value={selectedOption || ''}
            onValueChange={(value) => onSelectAmount(id, value === 'deposit' ? advanceDeposit : cartTotal)}
            disabled={isMutating}
            aria-label={`Montant à facturer pour ${name}`}
          >
            <AmountChoice
              id={id}
              value="deposit"
              title="Acompte"
              amount={advanceDeposit}
              disabled={isMutating}
              selected={selectedOption === 'deposit'}
              onEdit={selfRegistered ? openCustomInput : null}
            />
            <AmountChoice
              id={id}
              value="total"
              title="Paiement intégral"
              amount={cartTotal}
              disabled={isMutating}
              selected={selectedOption === 'total'}
            />
          </RadioGroup>
          {amountMode === 'custom' && !showCustom && (
            <div className="mt-2 flex items-center justify-between rounded-md border border-hajj-primary/25 bg-hajj-primary/5 px-3 py-2">
              <span className="text-xs font-medium text-hajj-primary">Montant personnalisé</span>
              <strong className="text-sm tabular-nums text-gray-900">{formatAmount(amount)}</strong>
            </div>
          )}
        </>
      )}

      {showCustom && requiresChoice && selfRegistered && (
        <div className="mt-3 rounded-md border border-hajj-primary/20 bg-hajj-primary/5 p-3">
          <label htmlFor={`custom-amount-${id}`} className="mb-1.5 block text-xs font-medium text-gray-700">
            Montant personnalisé pour {name}
          </label>
          <div className="flex items-center gap-2">
            <Input
              id={`custom-amount-${id}`}
              ref={customInputRef}
              type="number"
              min={advanceDeposit}
              max={cartTotal}
              step="1"
              value={customValue}
              onChange={(event) => {
                setCustomValue(event.target.value);
                setCustomError(null);
              }}
              placeholder="Montant"
              aria-label={`Montant personnalisé pour ${name}`}
              aria-invalid={Boolean(customError)}
              aria-describedby={customError ? `custom-amount-error-${id}` : undefined}
              disabled={isMutating}
            />
            <span className="shrink-0 text-sm font-medium text-gray-600">F CFA</span>
          </div>
          <p className="mt-1 text-xs text-gray-500">Minimum : {formatAmount(advanceDeposit)}</p>
          {customError && (
            <p id={`custom-amount-error-${id}`} className="mt-1 text-xs text-red-600" role="alert">
              {customError}
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isMutating}
              onClick={() => {
                setCustomError(null);
                onCancelCustom(id);
              }}
            >
              Annuler
            </Button>
            <Button type="button" size="sm" variant="hajj" onClick={validateCustomAmount} disabled={isMutating}>
              Valider
            </Button>
          </div>
        </div>
      )}
    </article>
  );
};

InvoicePilgrimRow.propTypes = {
  trip: PropTypes.shape({}).isRequired,
  advanceDeposit: PropTypes.number,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showCustom: PropTypes.bool,
  isPilgrim: PropTypes.bool,
  isMutating: PropTypes.bool,
  onSelectAmount: PropTypes.func.isRequired,
  onStartCustom: PropTypes.func.isRequired,
  onCommitCustom: PropTypes.func.isRequired,
  onCancelCustom: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const InvoiceSearchResult = ({ trip, onSelect }) => {
  const { firstname, lastname, npi, categoryName, depositTransferred, isInvoiceFullyPaid } = useGetPilgrim(trip);
  return (
    <Button
      type="button"
      variant="ghost"
      className="h-auto w-full justify-start gap-3 whitespace-normal px-0 py-2.5 text-left"
      onClick={() => onSelect(trip)}
    >
      <span className="min-w-0 flex-1">
        <strong className="block truncate text-sm">{getUserName(firstname, lastname)}</strong>
        <span className="block truncate text-xs text-gray-500">
          {[npi && `NPI ${npi}`, categoryName].filter(Boolean).join(' · ') || '—'}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>Payé : {formatAmount(Number(depositTransferred) || 0)}</span>
          {isInvoiceFullyPaid && <span className="font-medium text-emerald-700">Déjà soldé</span>}
        </span>
      </span>
      <span className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md border bg-white px-3 text-sm font-medium">
        <Plus className="size-4" /> Ajouter
      </span>
    </Button>
  );
};

InvoiceSearchResult.propTypes = { trip: PropTypes.shape({}).isRequired, onSelect: PropTypes.func.isRequired };

const InvoicePilgrimPicker = ({ excludedIds, onSelect, onClose }) => {
  const { api } = useAPI();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 1000);
  const effectiveSearch = search.trim() ? debouncedSearch : '';
  const fetchParams = useMemo(() => ({ q: effectiveSearch, limit: 20 }), [effectiveSearch]);
  const {
    data: trips,
    isFetching,
    isError,
  } = useQuery({
    queryFn: effectiveSearch ? api.list('trip/', fetchParams) : async () => [],
    queryKey: ['trip/invoice-picker/', fetchParams],
    keepPreviousData: true,
    enabled: Boolean(effectiveSearch),
  });
  const canShowResults = Boolean(search.trim()) && search === effectiveSearch;
  const availableTrips = useMemo(
    () => (canShowResults ? (trips || []).filter((trip) => !excludedIds.includes(trip.id)) : []),
    [canShowResults, excludedIds, trips]
  );

  return (
    <section className="mb-4 rounded-lg border border-hajj-primary/20 bg-hajj-primary/5 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-800">Rechercher un pèlerin</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={onClose}
          aria-label="Fermer la recherche"
        >
          <X className="size-4" />
        </Button>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && event.preventDefault()}
          placeholder="Rechercher un pèlerin (Nom, Prénoms, NPI, Passeport)"
          className="pl-9"
        />
      </div>
      <div className="mt-3 max-h-64 divide-y overflow-y-auto">
        {search.trim() && (isFetching || search !== effectiveSearch) && (
          <p className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
            <Spinner /> Chargement...
          </p>
        )}
        {canShowResults && !isFetching && isError && (
          <p className="py-4 text-center text-sm text-red-600">Impossible de charger les résultats.</p>
        )}
        {canShowResults && !isFetching && !isError && availableTrips.length === 0 && (
          <p className="py-4 text-center text-sm text-gray-500">
            {trips && trips.length > 0
              ? 'Tous les pèlerins de cette recherche sont déjà dans la facture'
              : 'Aucun pèlerin trouvé'}
          </p>
        )}
        {canShowResults &&
          !isFetching &&
          !isError &&
          availableTrips.map((trip) => <InvoiceSearchResult key={trip.id} trip={trip} onSelect={onSelect} />)}
      </div>
    </section>
  );
};

InvoicePilgrimPicker.propTypes = {
  excludedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onSelect: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const InvoiceModal = ({ isOpened = false, selectedPilgrimsData = [], advanceDeposit = 0, closeModal = () => {} }) => {
  const { api } = useAPI();
  const { isPilgrim } = useGetUser();
  const queryClient = useQueryClient();
  const [pilgrims, setPilgrims] = useState(selectedPilgrimsData || []);
  const [showPilgrimPicker, setShowPilgrimPicker] = useState(false);
  const [paymentState, setPaymentState] = useState(() => getInitialPaymentState(selectedPilgrimsData || []));
  const selectedPilgrimsRef = useRef(selectedPilgrimsData || []);
  const wasOpenRef = useRef(false);
  const isMutating = queryClient.isMutating() > 0;
  const pilgrimIds = useMemo(() => pilgrims.map((trip) => trip.id), [pilgrims]);
  const invoiceLines = useMemo(
    () =>
      pilgrims.map((trip) => ({
        tripId: trip.id,
        amount: getSelectedAmount(trip, paymentState[trip.id]?.amount, advanceDeposit),
      })),
    [advanceDeposit, paymentState, pilgrims]
  );
  const hasUnresolvedChoice = invoiceLines.some((line) => line.amount === null);
  const depositsToCreate = invoiceLines
    .filter((line) => Number(line.amount) > 0)
    .map((line) => ({ trip_id: line.tripId, amount: Number(line.amount) }));
  const total = depositsToCreate.reduce((sum, deposit) => sum + deposit.amount, 0);
  const hasOpenCustomAmount = Object.values(paymentState).some((payment) => payment.customOpen);

  useEffect(() => {
    selectedPilgrimsRef.current = selectedPilgrimsData || [];
  });

  useEffect(() => {
    if (isOpened && !wasOpenRef.current) {
      const nextPilgrims = selectedPilgrimsRef.current;
      setPilgrims(nextPilgrims);
      setPaymentState(getInitialPaymentState(nextPilgrims));
      setShowPilgrimPicker(false);
    }
    wasOpenRef.current = isOpened;
  }, [isOpened]);

  const { mutate: createInvoices } = useMutation({
    mutationFn: (deposits) => api.create('invoice/', { deposits }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['invoice/'] });
      queryClient.invalidateQueries({ queryKey: ['deposit/'] });
      queryClient.invalidateQueries({ queryKey: ['deposit/stats/default/'] });
      toast.success('Opération réussie !');
      closeModal();
    },
    onError: (error) => toast.error(error?.message || 'Erreur lors de la génération des factures'),
  });

  const handleAddPilgrim = (trip) => {
    setPilgrims((current) => (current.some((item) => item.id === trip.id) ? current : [...current, trip]));
    setPaymentState((current) => ({
      ...current,
      [trip.id]: { amount: getInitialAmount(trip), customOpen: false },
    }));
  };
  const handleRemovePilgrim = (id) => {
    setPilgrims((current) => current.filter((trip) => trip.id !== id));
    setPaymentState((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => String(key) !== String(id)))
    );
  };

  const handleSelectAmount = (id, amount) => {
    setPaymentState((current) => ({ ...current, [id]: { amount, customOpen: false } }));
  };

  const handleStartCustom = (id) => {
    setPaymentState((current) => ({
      ...current,
      [id]: { ...current[id], customOpen: true },
    }));
  };

  const handleCommitCustom = (id, amount) => {
    setPaymentState((current) => ({ ...current, [id]: { amount, customOpen: false } }));
  };

  const handleCancelCustom = (id) => {
    setPaymentState((current) => ({
      ...current,
      [id]: { ...current[id], customOpen: false },
    }));
  };
  const validateAndSubmit = () => {
    if (hasUnresolvedChoice || hasOpenCustomAmount) {
      toast.error('Veuillez saisir un montant pour chaque pèlerin');
      return;
    }
    if (depositsToCreate.length === 0) {
      toast.error('Aucun pèlerin n’a de montant à facturer');
      return;
    }
    createInvoices(depositsToCreate);
  };

  return (
    <Dialog open={isOpened} onOpenChange={closeModal}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-[820px] flex-col gap-0 overflow-hidden bg-white p-0 font-[Inter,sans-serif] sm:max-h-[calc(100dvh-2rem)]">
        <DialogHeader className="shrink-0 px-5 pb-4 pt-5 text-left sm:px-6">
          <DialogTitle>Générer les factures</DialogTitle>
          <DialogDescription>Choisissez le montant à facturer pour chaque pèlerin.</DialogDescription>
        </DialogHeader>
        <div className="flex shrink-0 flex-col items-stretch justify-between gap-2 px-5 pb-4 sm:flex-row sm:items-center sm:px-6">
          <span className="text-sm font-semibold text-gray-700">
            {pilgrims.length} pèlerin{pilgrims.length === 1 ? '' : 's'}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-hajj-primary/40 text-hajj-primary hover:bg-hajj-primary/5"
            onClick={() => setShowPilgrimPicker((open) => !open)}
            disabled={isMutating}
            aria-expanded={showPilgrimPicker}
          >
            <Plus className="size-4" /> Ajouter un pèlerin à la facture
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-5 pb-5 sm:px-6">
          {showPilgrimPicker && (
            <InvoicePilgrimPicker
              excludedIds={pilgrimIds}
              onSelect={handleAddPilgrim}
              onClose={() => setShowPilgrimPicker(false)}
            />
          )}
          {pilgrims.length === 0 ? (
            <div className="rounded-lg border border-dashed px-5 py-9 text-center text-sm text-gray-500">
              Aucun pèlerin dans la facture.
            </div>
          ) : (
            <div className="space-y-3">
              {pilgrims.map((trip) => (
                <InvoicePilgrimRow
                  key={trip.id}
                  trip={trip}
                  advanceDeposit={advanceDeposit}
                  amount={paymentState[trip.id]?.amount}
                  showCustom={Boolean(paymentState[trip.id]?.customOpen)}
                  isPilgrim={isPilgrim}
                  isMutating={isMutating}
                  onSelectAmount={handleSelectAmount}
                  onStartCustom={handleStartCustom}
                  onCommitCustom={handleCommitCustom}
                  onCancelCustom={handleCancelCustom}
                  onRemove={handleRemovePilgrim}
                />
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="shrink-0 flex-col items-stretch border-t bg-white px-5 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-6">
          <div className="mb-2 text-left sm:mb-0">
            <span className="block text-xs text-gray-500">Total à facturer</span>
            <strong className="block text-xl font-semibold tabular-nums text-hajj-primary">
              {formatAmount(total)}
            </strong>
            <span className="mt-1 block text-xs text-gray-500">
              {pilgrims.length} pèlerin{pilgrims.length === 1 ? '' : 's'} inclus
              {total <= 0 ? ' · Aucun montant à facturer.' : ''}
            </span>
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              type="button"
              variant="outline"
              className="flex-1 sm:flex-none"
              onClick={closeModal}
              disabled={isMutating}
            >
              Annuler
            </Button>
            <Button
              type="button"
              variant="hajj"
              className="flex-1 sm:flex-none"
              data-action="generate-invoices"
              onClick={validateAndSubmit}
              disabled={isMutating || hasUnresolvedChoice || hasOpenCustomAmount || total <= 0}
            >
              {isMutating ? 'Génération...' : 'Générer'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

InvoiceModal.propTypes = {
  isOpened: PropTypes.bool,
  selectedPilgrimsData: PropTypes.arrayOf(PropTypes.shape({})),
  advanceDeposit: PropTypes.number,
  closeModal: PropTypes.func,
};

export default InvoiceModal;
